import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";
import "../../styles/Form.css";
import logo from "../../images/logo.png";

const CustomTaskPage = () => {
  const { t_key, userId: paramUserId } = useParams();
  const navigate = useNavigate();

  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("token");

  const loggedInUserId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";

  const effectiveUserId = isAdmin && paramUserId ? paramUserId : loggedInUserId;

  const [task, setTask] = useState(null);
  const [user, setUser] = useState(null);

  // ✅ upload states (unchanged)
  const [file, setFile] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  // --------------------------------------------------
  // Load Custom Task
  // --------------------------------------------------
  useEffect(() => {
    axios
      .get(`${API_BASE}/api/custom-tasks/by-key/${t_key}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setTask(res.data))
      .catch(() => {
        alert("Task not found");
        navigate("/userhome");
      });
  }, [API_BASE, t_key, token, navigate]);

  // --------------------------------------------------
  // Load User (Employee Name)
  // --------------------------------------------------
  useEffect(() => {
    if (!effectiveUserId) return;

    axios
      .get(`${API_BASE}/api/users/${effectiveUserId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setUser(res.data))
      .catch(() => console.error("Failed to load user"));
  }, [API_BASE, effectiveUserId, token]);

  const handleCancel = () => {
    if (isAdmin && paramUserId) {
      navigate(`/userhome/${paramUserId}`);
    } else {
      navigate("/userhome");
    }
  };

  if (!task || !user) return null;

  const employeeName = `${user.first_name} ${user.last_name}`;
  const today = new Date().toLocaleDateString();

  const injectTaskVariables = (html) => {
    if (!html) return "";

    return html
      .replaceAll("{{employee_name}}", employeeName)
      .replaceAll("{{task_title}}", task.t_display_title)
      .replaceAll("{{date}}", today);
  };

  // ==================================================
  // Upload handler (unchanged logic)
  // ==================================================
  const handleUpload = async (e) => {
    e.preventDefault();

    if (!file) {
      alert("Please select a file");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("userId", effectiveUserId); // for DB
    formData.append("taskKey", task.t_key); // for DB
    formData.append("categoryId", task.t_category_id); // ta_context = category ID

    try {
      // ⬆️ Upload file
      await axios.post(
        `${API_BASE}/api/custom-tasks/upload-attachment?taskKey=${encodeURIComponent(
          task.t_key
        )}&userId=${effectiveUserId}`, // ✅ IMPORTANT
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      // 🔄 Reload attachments
      const refreshed = await axios.get(
        `${API_BASE}/api/custom-tasks/attachments/${encodeURIComponent(
          task.t_key
        )}/${effectiveUserId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (Array.isArray(refreshed.data)) {
        setUploadedFiles(refreshed.data);
      }

      setFile(null);
      alert("File uploaded successfully");
    } catch (err) {
      console.error("Upload failed", err);
      alert("Upload failed");
    }
  };

  return (
    <>
      {/* 🔝 TOP BAR */}
      <div className="d-flex justify-content-between align-items-center pt-3">
        <span style={{ fontSize: "18px", fontWeight: "500" }}>
          {task.t_display_title}
        </span>

        <button className="btn btn-secondary" onClick={handleCancel}>
          Return
        </button>
      </div>

      {/* 📄 TASK BODY */}
      <div
        className="form-wrapper my-4"
        style={{ fontSize: "15px", lineHeight: "1.6" }}
      >
        {/* HEADER */}
        <div className="row align-items-center mb-4">
          <div className="col-3">
            <img src={logo} alt="Logo" style={{ width: "150px" }} />
          </div>

          <div className="col-6 text-center">
            <h2 className="m-0">{task.t_display_title}</h2>
          </div>

          <div className="col-3 text-end">
            <strong>New-Way Trucking Ltd</strong>
            <br />
            7725 48 St. SE, Calgary
            <br />
            Alberta, Canada T2C 2V3
          </div>
        </div>

        {/* TASK CONTENT */}
        {task.t_pdf ? (
          <iframe
            src={`${API_BASE}${task.t_pdf}`}
            width="100%"
            height="800px"
            style={{ border: "none" }}
            title="Task PDF"
          />
        ) : (
          <div
            dangerouslySetInnerHTML={{
              __html: injectTaskVariables(task.t_content),
            }}
          />
        )}

        {/* ==================================================
            Upload Section (ONLY if enabled)
           ================================================== */}
        {task.t_allow_upload === 1 && (
          <>
            <hr className="my-4" />

            <h5>Attachments</h5>

            <form onSubmit={handleUpload}>
              <div className="mb-3">
                <label>Select File (PDF / JPG / PNG)</label>
                <input
                  type="file"
                  className="form-control"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setFile(e.target.files[0])}
                />
              </div>

              <div className="d-flex justify-content-end">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={!file}
                >
                  Upload
                </button>
              </div>
            </form>

            {uploadedFiles.length > 0 && (
              <div className="mt-4 p-3 bg-light rounded border">
                <div className="text-muted mb-2">Uploaded Files</div>

                <ul className="list-unstyled mb-0">
                  {uploadedFiles.map((f, idx) => (
                    <li key={idx}>
                      <a
                        href={`${API_BASE}/${f.ta_path}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        download={f.ta_original_name}
                      >
                        {f.ta_original_name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}

        <hr className="my-4" />

        {/* EMPLOYEE NAME + DATE */}
        <div className="row mb-4">
          <div className="col-md-6">
            <label className="form-label">Employee Name</label>
            <input
              type="text"
              className="form-control"
              value={employeeName}
              readOnly
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">Date</label>
            <input
              type="text"
              className="form-control"
              value={today}
              readOnly
            />
          </div>
        </div>

        <hr className="my-5" />

        {/* SIGNATURE + REVIEW (UNCHANGED) */}
        <SignatureManager
          taskId={task.t_id}
          taskName={task.t_key}
          userId={effectiveUserId}
          isAdmin={isAdmin}
        />

        <ReviewManager
          taskId={task.t_id}
          taskName={task.t_key}
          userId={effectiveUserId}
          isAdmin={isAdmin}
        />
      </div>
    </>
  );
};

export default CustomTaskPage;
